import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Fish } from "lucide-react";

import { cn } from "@/lib/utils"

/**
 * The Badge itself, apart from the status vocabulary (badge.tsx).
 *
 * Split out because the app shell draws badges on every screen, and the
 * shell is the first load: importing the Badge from badge.tsx also brought
 * the status-chip vocabulary and every shared value label (~8 KB gzip) for
 * statusLabel(), which no first screen calls. badge.tsx re-exports all of
 * this, so a page that wants both still imports one module.
 */
/**
 * A PILL, and the button is squared-off — on Andrew's call (2026-09-09), the
 * reverse of PropPay's pairing. What is load-bearing is only that the two
 * shapes differ: a control and a status must never share a silhouette, which
 * is what happened when both were 8px rectangles. No gradient and no hover: a
 * status is not a control.
 */
const badgeVariants = cva(
  // Whitespace-nowrap: a two-word status is one word to a reader.
  // A 1px hairline in the chip's own hue, on every variant. It was `border-0`
  // with flat tints, and the app ended up with two kinds of chip: the ones a
  // call site outlined by hand (`border-ocean/50`, the unit-status chips) and
  // the ones the primitive drew with no edge at all — Andrew read the second
  // kind as "missing their outline colour". Solid fills take a transparent
  // border so they keep the same box.
  // A pill, micro type, a tint (Andrew, 2026-11-02: chips go pill with the
  // buttons and tabs, as PropPay's are). It was a 4px-cornered label from
  // 2026-09-27; `px-2` so the text clears the curve.
  "whitespace-nowrap inline-flex shrink-0 items-center gap-1 rounded-full border border-transparent px-2 py-0.5 text-micro font-semibold leading-none focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
  {
    variants: {
      variant: {
        default: "bg-primary/[.12] text-primary-deep",
        secondary: "bg-foreground/[.07] text-muted-foreground",
        destructive: "bg-tint-destructive text-on-tint-destructive",

        outline: "border-border-strong/60 text-foreground",

        /* Semantic variants use the SEMANTIC tokens.
        
           These were defined with the ocean palette — success/seaweed,
           warning/starfish, danger/coral — which is where the app's colour drift
           started: this primitive is the pattern every call site copied, so a
           tick meaning "done" and a badge meaning "Qualified" ended up the same
           green, and `danger` was literally the same colour as the Firm buyer-
           journey stage. `success`, `warning`, `info` and `destructive` already
           existed as tokens and were barely used (49/45/8 references against
           637 for the ocean palette).
        
           The ocean palette keeps its real job — buyer-journey stages and unit
           status, where the colour IS the datum. See docs/design-system.md
           §Colour. */
        /* The status PAIRS (index.css): a tint and the ink measured on it.
           A role colour at 12% under the same role as text lost contrast —
           destructive 3.92:1, success 4.25:1 in light (axe). Colour is never
           alone — the label carries the meaning, the tint only groups it. */
        success: "bg-tint-success text-on-tint-success",
        warning: "bg-tint-warning text-on-tint-warning",
        danger:  "bg-tint-destructive text-on-tint-destructive",
        info:    "bg-tint-info text-on-tint-info",
        /* `accent` is decorative by definition, so it keeps a palette hue. */
        accent:  "bg-seahorse/[.12] text-seahorse",
        subtle:  "bg-foreground/[.06] text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  preserveCase?: boolean;
}

function Badge({ className, variant, children, preserveCase = false, ...props }: BadgeProps) {
  /*
    Sentence case, as everything else is (2026-10-28, tests/sentenceCase.test.ts).
    A badge used to Title Case every word of its text, so a t() string written
    "Possible match" or "Ready to send" read "Possible Match" / "Ready To Send",
    and lowercased the rest of each word ("McDonald" → "Mcdonald"). Now the text
    is drawn as written; only a raw lowercase value ("pending") gets its first
    letter raised. `preserveCase` skips even that.
  */
  const properCaseChildren = (typeof children === 'string' && !preserveCase)
    ? children.replace(/\bsalefish\b/gi, 'SaleFish').replace(/^\p{Ll}/u, (c) => c.toUpperCase())
    : children;

  // Check if this is a SaleFish Admin badge
  const isSaleFishAdmin = typeof children === 'string' && children.toLowerCase().includes('salefish') && children.toLowerCase().includes('admin');

  return (
    /*
      `leading-none` is re-asserted AFTER the caller's classes. A chip is one
      line, and 26 call sites pass a type token such as `text-caption` —
      which tailwind-merge reads as overriding the base `leading-none`, so each
      of those chips took the token's line height and stood 27px tall beside
      the 22px ones. That is why chips were "different sizes" across the app
      (2026-09-15). A caller may still change the font size; not the height.
    */
    <div className={cn(badgeVariants({ variant }), className, "leading-none")} {...props}>
      {properCaseChildren}
      {isSaleFishAdmin && (
        <Fish className="ml-1 h-3 w-3" />
      )}
    </div>
  );
}

export { Badge, badgeVariants }
